import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiClientError } from '@/lib/api-client';
import {
  fetchMassBalance,
  fetchSnapshotHistory,
  runReconciliation,
  requestClose,
  closeBatch,
} from './api';
import type {
  MassBalanceViewDto,
  ReconciliationResultDto,
  ReconciliationSnapshotDto,
} from './types';

const KEY = 'mass-balance' as const;

export const useMassBalance = (batchId: string | undefined) => {
  return useQuery<MassBalanceViewDto, ApiClientError>({
    queryKey: [KEY, 'view', batchId],
    queryFn: () => fetchMassBalance(batchId!),
    enabled: !!batchId,
  });
};

export const useSnapshotHistory = (batchId: string | undefined) => {
  return useQuery<ReconciliationSnapshotDto[], ApiClientError>({
    queryKey: [KEY, 'snapshots', batchId],
    queryFn: () => fetchSnapshotHistory(batchId!),
    enabled: !!batchId,
  });
};

export const useRunReconciliation = () => {
  const qc = useQueryClient();
  return useMutation<ReconciliationResultDto, ApiClientError, string>({
    mutationFn: (batchId) => runReconciliation(batchId),
    onSuccess: (_, batchId) => {
      qc.invalidateQueries({ queryKey: [KEY, 'view', batchId] });
      qc.invalidateQueries({ queryKey: [KEY, 'snapshots', batchId] });
      qc.invalidateQueries({ queryKey: ['exceptions'] });
    },
  });
};

export const useRequestClose = () => {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (batchId) => requestClose(batchId),
    onSuccess: (_, batchId) => {
      qc.invalidateQueries({ queryKey: [KEY, 'view', batchId] });
    },
  });
};

export const useCloseBatch = () => {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (batchId) => closeBatch(batchId),
    onSuccess: (_, batchId) => {
      qc.invalidateQueries({ queryKey: [KEY, 'view', batchId] });
    },
  });
};
